// components/ui/HospitalSelector.tsx

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MapPin, Loader2, Building2 } from "lucide-react";
import { useLocation } from "@/contexts/LocationContext";

export default function HospitalSelector() {
  const {
    selectedLocationId,
    selectedHospitalName,
    locations,
    loading,
    setSelectedLocation,
    setSelectedHospitalName,
  } = useLocation();

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-white/60">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">Loading...</span>
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <div className="flex items-center gap-2 text-white/50">
        <span className="text-sm">No locations available</span>
      </div>
    );
  }

  const hospitals = Array.from(new Set(locations.map(loc => loc.hospitalName).filter(Boolean))).sort() as string[];
  const filteredLocations = locations.filter(loc => loc.hospitalName === selectedHospitalName);

  // Shared trigger style: transparent bg, white border, white text — fits the dark green header
  const triggerClass =
    "h-9 bg-white/10 hover:bg-white/15 border-white/25 hover:border-white/40 text-white focus:ring-white/20 focus:border-white/50 rounded-lg w-full transition-colors";

  return (
    <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
      {/* Hospital Selector */}
      <div className="flex-1 min-w-0 max-w-[180px] md:max-w-[200px]">
        <Select
          value={selectedHospitalName}
          onValueChange={(val) => {
            setSelectedHospitalName(val);
            const firstLoc = locations.find(l => l.hospitalName === val);
            if (firstLoc) setSelectedLocation(firstLoc.value);
          }}
        >
          <SelectTrigger className={triggerClass}>
            <div className="flex items-center gap-1.5 w-full min-w-0 font-medium">
              <Building2 className="w-3.5 h-3.5 text-white/70 flex-shrink-0" />
              <div className="truncate flex-1 text-left text-xs md:text-sm text-white">
                <SelectValue placeholder="Hospital" />
              </div>
            </div>
          </SelectTrigger>
          <SelectContent>
            {hospitals.map((h) => (
              <SelectItem key={h} value={h}>
                {h}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Branch Selector */}
      <div className="flex-1 min-w-0 max-w-[220px] md:max-w-[240px]">
        <Select
          value={selectedLocationId ? String(selectedLocationId) : undefined}
          onValueChange={(val) => setSelectedLocation(Number(val))}
          disabled={!selectedHospitalName}
        >
          <SelectTrigger className={triggerClass}>
            <div className="flex items-center gap-1.5 w-full min-w-0">
              <MapPin className="w-3.5 h-3.5 text-white/60 flex-shrink-0" />
              <div className="truncate flex-1 text-left text-xs md:text-sm text-white">
                <SelectValue placeholder="Branch" />
              </div>
            </div>
          </SelectTrigger>
          <SelectContent className="max-w-[300px] md:max-w-[400px]">
            {filteredLocations.map((loc) => (
              <SelectItem
                key={loc.value}
                value={String(loc.value)}
                className="truncate"
              >
                {loc.branchName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

